import React from "react";

/**
 * Privacy Policy — public page linked from Twilio A2P Campaign Registration
 * and site footer. The language below intentionally includes the three
 * carrier-required statements (mobile-number non-sharing, message frequency,
 * "message and data rates may apply") so Twilio approves the campaign.
 */
export default function Privacy() {
  return (
    <section
      data-testid="privacy-page"
      className="mx-auto max-w-3xl px-5 sm:px-8 py-14 text-white/85 space-y-6 leading-relaxed"
    >
      <header>
        <div className="label-caps text-cyan text-xs">Legal</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-white mt-2">Privacy Policy</h1>
        <p className="text-sm text-white/50 mt-2">Effective February 1, 2026</p>
      </header>

      <p>
        Plenty Rydez LLC ("Plenty Rydez", "we", "us") operates <a className="text-cyan hover:underline" href="https://plenty-rydez.com">plenty-rydez.com</a> and
        the Plenty Rydez mobile web app (the "Services"). This policy explains
        what personal data we collect, how we use it, and the choices you have.
      </p>

      <h2 className="font-serif text-2xl text-white mt-8">1. Information we collect</h2>
      <p>
        When you book a chauffeured ride, self-drive rental, or submit an
        inquiry, we collect the name, phone number, email address, driver's
        license (for rentals), pickup / return locations, and payment details
        you provide. We also collect basic device and analytics data so we can
        run the site.
      </p>

      <h2 className="font-serif text-2xl text-white mt-8">2. SMS &amp; messaging program</h2>
      <p>
        By providing your phone number and requesting a booking or rental link,
        you agree to receive transactional SMS messages from Plenty Rydez —
        including booking confirmations, rental agreement links, driver ETA
        updates, and manager follow-ups.
      </p>
      <ul className="list-disc pl-6 space-y-2 text-white/80">
        <li><b>Message frequency:</b> Message frequency varies based on your booking activity. Typical customers receive 2–5 messages per reservation.</li>
        <li><b>Message and data rates may apply.</b> Standard carrier rates from your mobile provider apply to each message received.</li>
        <li><b>Opt-out:</b> Reply <span className="font-mono text-cyan">STOP</span> to any Plenty Rydez SMS at any time to cancel further messages. Reply <span className="font-mono text-cyan">HELP</span> for help. You can also email <a className="text-cyan hover:underline" href="mailto:concierge@plenty-rydez.com">concierge@plenty-rydez.com</a>.</li>
        <li><b>Non-sharing of mobile numbers:</b> <b>Plenty Rydez does not share, sell, rent, or lease mobile phone numbers or SMS opt-in data with any third parties or affiliates for marketing purposes.</b> All mobile-number data stays inside Plenty Rydez systems and is used only to fulfill your reservation.</li>
      </ul>

      <h2 className="font-serif text-2xl text-white mt-8">3. How we use your information</h2>
      <ul className="list-disc pl-6 space-y-2 text-white/80">
        <li>Fulfilling bookings, rentals, and inquiries.</li>
        <li>Sending transactional SMS and email required for your reservation.</li>
        <li>Verifying driver's license identity for self-drive rentals.</li>
        <li>Processing payments through Stripe, PayPal, Cash App, Apple Pay, and Zelle.</li>
        <li>Complying with law and defending Plenty Rydez against fraud.</li>
      </ul>

      <h2 className="font-serif text-2xl text-white mt-8">4. Sharing with service providers</h2>
      <p>
        We share the minimum necessary information with vetted subprocessors
        (Twilio for SMS, Resend for email, Stripe / PayPal for payments,
        Google Maps for routing, Emergent Object Storage for uploaded photos).
        None of these subprocessors are permitted to use your data for their
        own marketing.
      </p>

      <h2 className="font-serif text-2xl text-white mt-8">5. Retention</h2>
      <p>
        We retain booking and rental agreement records for 7 years to comply
        with tax and insurance obligations. You may request deletion of
        marketing-related data at any time by writing to
        <a className="text-cyan hover:underline ml-1" href="mailto:concierge@plenty-rydez.com">concierge@plenty-rydez.com</a>.
      </p>

      <h2 className="font-serif text-2xl text-white mt-8">6. Your rights</h2>
      <p>
        You can access, correct, or delete your personal information by
        contacting our concierge team. California, Colorado, and EU residents
        additionally have the rights afforded by the CCPA, CPA, and GDPR.
      </p>

      <h2 className="font-serif text-2xl text-white mt-8">7. Contact</h2>
      <p>
        Plenty Rydez LLC · Serving NY / NJ / PA / DMV / CT<br />
        Email: <a className="text-cyan hover:underline" href="mailto:concierge@plenty-rydez.com">concierge@plenty-rydez.com</a><br />
        Phone: <a className="text-cyan hover:underline" href="tel:+17188409084">(718) 840-9084</a>
      </p>
    </section>
  );
}
